'use client';

import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useFeedback } from 'providers/feedback-provider';
import { useMyEquipmentDrawer } from 'components/my-equipment/my-equipment-drawer-context';
import { BlocksTabChange } from '@blocks-web-components/events/blocks-tab-change';
import { AddEquipmentBySerial } from './add-equipment-by-serial/add-equipment-by-serial';
import { AddEquipmentByModel } from './add-equipment-by-model/add-equipment-by-model';
import { ErrorBannerMessage } from 'components/my-equipment/error-banner-message';
import { AddEquipmentFootNote } from '../add-equipment-foot-note';
import { selectEquipment } from 'services/equipment/actions';
import { DrawerLoader } from 'components/my-equipment/drawer-loader';
import {
  DrawerBody,
  MyEquipmentSaveFooter,
  MyEquipmentTabs,
  MyEquipmentNickname,
  MyEquipmentToggleShareButton,
  MyEquipmentInfoToolTip,
  Conditional
} from 'components/pcc-components';
import {
  FORM_FIELD_NICK_NAME,
  SELECTED_EQUIPMENT,
  NOTIFY_DEALER,
  TAB_INDEX_SERIAL,
  DUPLICATE_EQUIPMENT_ERROR
} from 'components/my-equipment/constants';
import { UNKNOWN_ERROR_MESSAGE } from 'constants/logging';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import { useQuickToast } from 'hooks/use-toast';
import { useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { HEADLESS_SAVED_LISTS } from 'constants/links';
import { checkIsAlpPage, getAlpSeoUrl } from 'components/my-equipment/utils';

export const AddEquipmentForm = ({
  isCspUser = false
}: {
  isCspUser: boolean;
}) => {
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.XL);
  const t = useTranslations();
  const form = useFormContext();
  const router = useRouter();
  const watchSelectedEquipment = form.watch(SELECTED_EQUIPMENT);
  const watchNotifyDealer = form.watch(NOTIFY_DEALER);
  const { defaultEquipmentModel } = useMyEquipmentDrawer();
  const defaultTabIndex = defaultEquipmentModel ? 1 : TAB_INDEX_SERIAL;
  const [activeIndex, setActiveIndex] = useState(defaultTabIndex);
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [
    showSearchSuggestionFailureBanner,
    setShowSearchSuggestionFailureBanner
  ] = useState(false);
  const {
    addEquipmentDrawer,
    selectedEquipmentDrawer,
    selectEquipmentDrawer,
    isLoading: isDrawerLoading
  } = useMyEquipmentDrawer();
  const pathname = usePathname();
  const params = useParams();
  const { isAlpSubpage, isAlp } = checkIsAlpPage(params, pathname);
  const { dismissFeedback, showFeedback, feedbackMessage } = useFeedback();
  const showNotifyDealer = isCspUser && activeIndex === TAB_INDEX_SERIAL;
  const showDuplicateEquipmentBanner =
    showFeedback && feedbackMessage === DUPLICATE_EQUIPMENT_ERROR;
  const showErrorBanner =
    showSearchSuggestionFailureBanner ||
    (showFeedback && feedbackMessage === UNKNOWN_ERROR_MESSAGE);
  const toast = useQuickToast();
  const handleClose = () => addEquipmentDrawer.closeDrawer();

  const showAddEquipmentToast = (selectedEquipmentValues) => {
    const { model = '', equipmentFamily = '' } = selectedEquipmentValues || {};
    const currentlyShoppingText = t('CURRENTLY_SHOPPING', {
      0: model,
      1: capitalizeText(equipmentFamily)
    });
    toast.success({
      message: currentlyShoppingText,
      delay: 5
    });
  };

  const onTabChange = (e: BlocksTabChange) => {
    setActiveIndex(e.detail.activeTabIndex);
    form.reset();
    setShowSearchSuggestionFailureBanner(false);
    dismissFeedback();
  };

  const handleNotifyDealerToggleChange = () => {
    form.setValue(NOTIFY_DEALER, !watchNotifyDealer);
  };

  const handleYesClickedDuplicateBanner = async () => {
    dismissFeedback();
    if (watchSelectedEquipment != null) {
      const equipmentId =
        watchSelectedEquipment.productId || watchSelectedEquipment.partNumber;
      await selectEquipment(equipmentId, watchSelectedEquipment?.serialNumber);
      showAddEquipmentToast(watchSelectedEquipment);
    }
    addEquipmentDrawer.closeDrawer();
    selectedEquipmentDrawer.closeDrawer();
    selectEquipmentDrawer.closeDrawer();
    if (isAlpSubpage || isAlp) {
      const redirectUrl = getAlpSeoUrl(
        watchSelectedEquipment.seoUrl,
        pathname,
        params,
        watchSelectedEquipment?.serialNumber
      );
      router.push(redirectUrl);
    }
  };

  useEffect(() => {
    setDisableSaveButton(watchSelectedEquipment === null);
  }, [watchSelectedEquipment]);

  return (
    <>
      <DrawerLoader isLoading={isDrawerLoading} />
      <DrawerBody
        className={`${isDrawerLoading ? 'd-none' : 'd-block h-100 position-relative'}`}
      >
        <Conditional test={showErrorBanner}>
          <ErrorBannerMessage
            buttonLabel={t('FOOTER_CONTACT_US')}
            bannerLabel={t('UNKNOWN_ERROR_MESSAGE')}
          />
        </Conditional>
        <MyEquipmentTabs activeIndex={activeIndex} onBlTabChange={onTabChange}>
          <AddEquipmentBySerial
            setShowSearchSuggestionFailureBanner={
              setShowSearchSuggestionFailureBanner
            }
            handleYesClickedDuplicateBanner={handleYesClickedDuplicateBanner}
            showBackButton
            isChild
          />

          <AddEquipmentByModel
            setShowSearchSuggestionFailureBanner={
              setShowSearchSuggestionFailureBanner
            }
            handleYesClickedDuplicateBanner={handleYesClickedDuplicateBanner}
            showBackButton
            isChild
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        </MyEquipmentTabs>
        <MyEquipmentNickname
          placeholder={t('MEQ_NICKNAME')}
          name={FORM_FIELD_NICK_NAME}
        />
        {!pathname.includes(HEADLESS_SAVED_LISTS) && (
          <>
            <Conditional test={showNotifyDealer}>
              <div className="pt-4 d-flex" data-testid="share-toggle">
                <MyEquipmentToggleShareButton
                  handleToggleChange={handleNotifyDealerToggleChange}
                  text={t('MEQ_ADD_NOTIFICATION1')}
                  checked={watchNotifyDealer}
                />
                <MyEquipmentInfoToolTip
                  label={t('MEQ_ADD_NOTIFICATION3')}
                  className="pt-1"
                  isInteractive={!isDesktop}
                />
              </div>
            </Conditional>
            <AddEquipmentFootNote />
          </>
        )}
      </DrawerBody>

      <Conditional test={!isDrawerLoading}>
        <MyEquipmentSaveFooter
          disableSave={disableSaveButton || showDuplicateEquipmentBanner}
          saveLabel={t('MSTLST_ADD')}
          cancelClass={'ps-3'}
          cancelLabel={t('CANCEL')}
          onCancelHandler={handleClose}
        />
      </Conditional>
    </>
  );
};
